import { createSelector } from "reselect";
import fetchers, { MODULES } from "./services";
import React from "react";
import { HeaderTag } from "components/common/ClusterProfileCard";

function findRepositoryByUid(repos, repoUid) {
  return repos.find((repo) => repo.metadata.uid === repoUid);
}

export const getAllRepos = createSelector(
  fetchers.allRepositoriesFetcher.selector,
  fetchers.ociRepositoriesFetcher.selector,
  ({ result: allRepos }, { result: ociRepos }) => {
    return (allRepos || []).map((repo) => {
      const ociRepo = (ociRepos || []).find(
        (ociRepo) => repo.uid === ociRepo.metadata.uid
      );

      if (ociRepo?.spec?.providerType === "zarf") {
        return {
          ...repo,
          kind: "zarf",
        };
      }

      if (
        ociRepo?.spec?.providerType === "pack" ||
        ociRepo?.spec?.providerType === "helm"
      ) {
        return {
          ...repo,
          kind: ociRepo?.spec?.providerType,
          isOci: true,
        };
      }

      return repo;
    });
  }
);

export const getAllReposByPackType = createSelector(
  getAllRepos,
  (state) => state.forms.layerPack?.data?.packType,
  (state) => state.forms.layerPack?.data?.accessType,
  (repos, packType, accessType) => {
    let type = packType;
    const isPrivatePack = accessType === "private";

    if (!type) return [];

    let allowedTypes = ["pack"];

    if (packType === "helmChart") {
      allowedTypes = ["helm"];
    }

    if (packType === "zarf") {
      allowedTypes = ["zarf"];
    }

    return [
      ...(repos || [])
        .filter((repo) =>
          packType === "helmChart" && isPrivatePack ? !!repo?.isOci : true
        )
        .filter((repo) => allowedTypes.includes(repo.kind))
        .map((repoInfo) => ({
          ...repoInfo,
          metadata: { uid: repoInfo.uid, name: repoInfo.name },
        })),
    ];
  }
);

export const getAvailableRepositories = createSelector(
  getAllReposByPackType,
  (repos) => {
    return (
      repos?.map((repo) => ({
        ...repo,
        label: (
          <>
            {repo.metadata.name}{" "}
            {repo.isOci && repo.kind !== "helm" ? (
              <HeaderTag inline>OCI</HeaderTag>
            ) : null}
          </>
        ),
        value: repo.metadata.uid,
        cleanLabel: repo.metadata.name,
      })) || []
    );
  }
);

export const arePacksLoading = createSelector(
  fetchers.clusterPacksFetcher.selector,
  (state) => state.forms?.importedPacks?.data?.clusterId,
  (result, clusterId) => result[clusterId]?.isLoading
);

export const getSelectedRepository = createSelector(
  (state) => state.forms[MODULES.PACK_MODULE]?.data?.repository,
  getAvailableRepositories,
  (repoUid, repos) => findRepositoryByUid(repos, repoUid)
);

export const getAllSystemRepositories = createSelector(
  fetchers.allRepositoriesFetcher.selector,
  ({ result: repos = [] }) => {
    return [
      ...(repos || [])
        .filter((repo) => ["oci", "helm"].includes(repo.kind))
        .map((repoInfo) => ({
          ...repoInfo,
          metadata: { uid: repoInfo.uid, name: repoInfo.name },
        })),
    ].map((repo) => ({
      ...repo,
      label: repo.metadata.name,
      value: repo.metadata.uid,
    }));
  }
);

export const getSelectedSystemRepository = createSelector(
  (state) => state.forms[MODULES.SYSTEM_MODULE]?.data?.repository,
  getAllSystemRepositories,
  (repoUid, repos) => findRepositoryByUid(repos, repoUid)
);

export const getSystemSelectedPack = createSelector(
  (state) => fetchers.systemChartNamesFetcher.selector(state)?.result?.items,
  (state) => state.forms[MODULES.SYSTEM_MODULE]?.data.pack,
  (items = [], packName) => {
    return items.find((pack) => pack.spec.name === packName);
  }
);
